<template>
  <lb-dialog
    :value="value"
    @input="handleDialogClose"
    :heading="heading || `${$nova.labelsName('my_reporting_units_page', 'ru_entity_label', 'RU')} Info`"
    width="60%"
    :loading="isLoading"
    data-testid="ru-info-dialog"
  >
    <template v-slot:body>
      <!-- Loading State -->
      <div v-if="isLoading" class="d-flex flex-column gap-4" data-testid="ru-info-dialog__skeleton">
        <v-skeleton-loader type="heading" width="40%" class="mb-1"></v-skeleton-loader>
        <div class="d-flex gap-4">
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
        </div>
      </div>

      <!-- Error State -->
      <div 
        v-else-if="hasError" 
        class="d-flex flex-column align-center justify-center py-8"
        data-testid="ru-info-dialog__error"
      >
        <v-icon color="error" size="48" class="mb-4">mdi-alert-circle-outline</v-icon>
        <span class="text-body-1 text--secondary">{{ errorMessage }}</span>
        <v-btn 
          text 
          color="primary" 
          class="mt-4"
          @click="fetchInsightsData"
          data-testid="ru-info-dialog__retry-button"
        >
          Retry
        </v-btn>
      </div>

      <!-- Empty State -->
      <div 
        v-else-if="isEmptyData" 
        class="d-flex flex-column align-center justify-center py-8"
        data-testid="ru-info-dialog__empty"
      >
        <v-icon color="grey" size="48" class="mb-4">mdi-information-outline</v-icon>
        <span class="text-body-1 text--secondary">No reporting unit insights available</span>
      </div>

      <!-- Content State -->
      <div v-else class="d-flex flex-column gap-4" data-testid="ru-info-dialog__content">
        <lb-datatablenew
          :headers="tableHeaders"
          :items="tableItems"
          :loading="isLoading"
          :hide-default-footer="true"
          :enableslot="enabledSlots"
          data-testid="ru-info-dialog__table"
        >
          <template v-slot:ru_code_description="{ item }">
            <span data-testid="ru-info-dialog__table-ru-code">
              {{ item.code }} - {{ item.name }}
            </span>
          </template>
          <template v-slot:network="{ item }">
            <span data-testid="ru-info-dialog__table-network">
              {{ item.network_detail?.name || item.network || '-' }}
            </span>
          </template>
          <template v-slot:region="{ item }">
            <span data-testid="ru-info-dialog__table-region">
              {{ item.region_detail?.name || item.region || '-' }}
            </span>
          </template>
          <template v-slot:country="{ item }">
            <span data-testid="ru-info-dialog__table-country">
              {{ item.country_detail?.name || item.country || '-' }}
            </span>
          </template>
          <template v-slot:type="{ item }">
            <span data-testid="ru-info-dialog__table-type">
              {{ item.type || '-' }}
            </span>
          </template>
        </lb-datatablenew>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { RU_INFO_DIALOG } from '../utils/constants';

export default {
  name: 'RUInfoDialog',

  props: {
    /** Controls dialog visibility */
    value: {
      type: Boolean,
      default: false,
    },
    /** Reporting Unit ID for fetching insights */
    ruId: {
      type: String,
      default: '',
    },
    /** Reporting Unit Type (e.g., 'soxid', 'ru') */
    ruType: {
      type: String,
      default: RU_INFO_DIALOG.DEFAULT_RU_TYPE,
    },
    /** Client ID for the API request */
    clientId: {
      type: String,
      default: '',
    },
    /** Custom dialog heading */
    heading: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      /** Error state flag */
      hasError: false,
      /** Error message to display */
      errorMessage: '',
    };
  },

  computed: {
    ...mapGetters('varianceanalysis/reportingUnit', [
      'getRUInsights',
      'getRUInsightsLoading',
    ]),

    /**
     * Computed headers based on RU type
     */
    tableHeaders() {
      const headers = [...RU_INFO_DIALOG.TABLE_HEADERS];
      if (this.ruType === 'jv') {
        headers.unshift({ text: 'Type', value: 'type', sortable: false });
      }
      return headers;
    },

    /**
     * Computed slots based on RU type
     */
    enabledSlots() {
      const slots = ['ru_code_description', 'network', 'region', 'country'];
      if (this.ruType === 'jv') {
        slots.push('type');
      }
      return slots;
    },

    /**
     * Returns the loading state from Vuex store
     * @returns {boolean}
     */
    isLoading() {
      return this.getRUInsightsLoading;
    },

    /**
     * Returns the RU insights data from Vuex store
     * @returns {Object}
     */
    insightsData() {
      return this.getRUInsights || {};
    },

    /**
     * Formats insights data for table display
     * @returns {Array}
     */
    tableItems() {
      if (!this.insightsData || Object.keys(this.insightsData).length === 0) {
        return [];
      }
      let data = [];
      if (Array.isArray(this.insightsData)) {
        data = this.insightsData;
      } else {
        data = [this.insightsData];
      }
      
      return data.map(item => ({
        ...item,
        ru_code_description: `${item.code || ''} - ${item.name || ''}`
      }));
    },

    /**
     * Checks if the data is empty
     * @returns {boolean}
     */
    isEmptyData() {
      return !this.hasError && this.tableItems.length === 0;
    },
  },

  watch: {
    /**
     * Watches dialog visibility and fetches data when opened
     * @param {boolean} newValue - New dialog visibility state
     */
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.fetchInsightsData();
        }
      },
    },
  },

  beforeDestroy() {
    // Reset component state on destroy to prevent stale data
    this.resetComponentState();
  },

  methods: {
    ...mapActions('varianceanalysis/reportingUnit', [
      'fetchRUInsights',
    ]),
    ...mapMutations('varianceanalysis/reportingUnit', [
      'RESET_RU_INSIGHTS',
    ]),

    /**
     * Fetches RU insights data from the API via Vuex action
     * API requires ruId and ruType (valid values: 'soxid', 'jv')
     */
    async fetchInsightsData() {
      // Reset error state before fetching
      this.hasError = false;
      this.errorMessage = '';

      // Get axios instance - VueAxios makes it available as this.axios
      const axiosInstance = this.axios || this.$axios || this.$http;

      // Validate required parameters
      if (!this.ruId) {
        console.warn('[RUInfoDialog]: Missing ruId - cannot fetch insights');
        this.hasError = true;
        this.errorMessage = 'Reporting Unit ID is not available. Please select a specific reporting unit.';
        return;
      }

      if (!this.ruType || this.ruType === 'all') {
        console.warn('[RUInfoDialog]: Missing or invalid ruType - cannot fetch insights. Type:', this.ruType);
        this.hasError = true;
        this.errorMessage = 'Reporting Unit type is not available. Please select a specific type filter (SOXID or JV) instead of "All".';
        return;
      }

      if (!axiosInstance) {
        console.error('[RUInfoDialog]: Axios instance not available');
        this.hasError = true;
        this.errorMessage = 'HTTP client not available';
        return;
      }

      try {
        await this.fetchRUInsights({
          filter: {
            ruId: this.ruId,
            ruType: this.ruType,
          },
          client: this.clientId,
          axiosInstance,
        });
      } catch (error) {
        console.error('[RUInfoDialog]: Failed to fetch RU insights', error);
        this.hasError = true;
        this.errorMessage = error.message || RU_INFO_DIALOG.ERROR_MESSAGE;
      }
    },

    /**
     * Handles dialog close event
     * @param {boolean} value - Dialog visibility state
     */
    handleDialogClose(value) {
      this.$emit('input', value);
      if (!value) {
        this.resetComponentState();
      }
    },

    /**
     * Resets component state to default values
     */
    resetComponentState() {
      this.hasError = false;
      this.errorMessage = '';
      this.RESET_RU_INSIGHTS();
    },
  },
};
</script>

<style scoped>

</style>
